import { useState } from "react";
import { motion } from "framer-motion";
import { ChevronRight } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { scrollToId, waLink, GENERAL_MESSAGE } from "@/lib/store";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { ThemeToggle } from "./ThemeToggle";
import { CartButton } from "./CartButton";
import { SearchButton } from "./SearchOverlay";
import { EASE } from "./Reveal";

const LINKS = [
  { id: "estoque", label: "Novos", cond: "novo", key: "novos" },
  { id: "estoque", label: "Seminovos", cond: "seminovo", key: "seminovos" },
  { id: "estoque", label: "Acessórios", cond: "acessorios", key: "acessorios" },
  { id: "estoque", label: "Estoque", cond: "todos", key: "estoque" },
  { id: "pedido", label: "Fazer pedido", key: "pedido" },
  { path: "/troca", label: "Troca", key: "troca" },
  { path: "/rastreio", label: "Rastreio", key: "rastreio" },
  { id: "por-que", label: "Por que a RC", key: "por-que" },
  { id: "contato", label: "Contato", key: "contato" },
];

const MobileTabs = ({ go }) => {
  const [atEnd, setAtEnd] = useState(false);
  const [atStart, setAtStart] = useState(true);
  const onScroll = (e) => {
    const el = e.currentTarget;
    setAtStart(el.scrollLeft < 8);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 8);
  };
  return (
    <nav data-testid="header-mobile-tabs" className="relative mt-2 overflow-hidden rounded-full border border-white/10 bg-black/60 backdrop-blur-xl lg:hidden">
      <div onScroll={onScroll} className="no-scrollbar flex snap-x snap-mandatory gap-1 overflow-x-auto scroll-px-1.5 px-1.5 py-1.5">
        {LINKS.map((l) => (
          <button key={l.key} data-testid={`mobile-nav-${l.key}`} onClick={() => go(l)} className="shrink-0 snap-start rounded-full px-3.5 py-1.5 text-[13px] font-medium text-zinc-200 transition-colors hover:bg-white/10 hover:text-white active:bg-white/15">{l.label}</button>
        ))}
        <span className="w-8 shrink-0" aria-hidden="true" />
      </div>
      <span className={`pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-[#0b0b0d] to-transparent transition-opacity duration-300 ${atStart ? "opacity-0" : "opacity-100"}`} />
      <span data-testid="mobile-tabs-more" className={`pointer-events-none absolute inset-y-0 right-0 flex w-14 items-center justify-end bg-gradient-to-l from-[#0b0b0d] via-[#0b0b0d]/80 to-transparent pr-2.5 text-zinc-300 transition-opacity duration-300 ${atEnd ? "opacity-0" : "opacity-100"}`}>
        <ChevronRight className="h-4 w-4 animate-pulse" />
      </span>
    </nav>
  );
};

export const Header = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const onHome = pathname === "/";
  const go = (l) => {
    if (l.path) {
      navigate(l.path);
      return;
    }
    if (!onHome) {
      navigate(`/${l.cond ? `?cond=${l.cond}` : ""}#${l.id}`);
      return;
    }
    if (l.cond) window.dispatchEvent(new CustomEvent("rc-filter", { detail: l.cond }));
    scrollToId(l.id);
  };
  const linkCls = "link-underline shrink-0 text-sm text-zinc-300 hover:text-white transition-colors";

  return (
  <motion.header
    data-testid="header-nav"
    initial={{ y: -80, opacity: 0 }}
    animate={{ y: 0, opacity: 1 }}
    transition={{ duration: 1, delay: onHome ? 0.9 : 0, ease: EASE }}
    className="fixed top-0 inset-x-0 z-50"
  >
    <div className="mx-auto max-w-[1440px] px-4 sm:px-8 pt-[calc(env(safe-area-inset-top)+12px)]">
      <div className="glass-bar flex items-center justify-between rounded-full border border-white/10 bg-black/60 backdrop-blur-xl pl-4 pr-2 py-2 sm:pl-5">
        <Link data-testid="header-logo" to="/" onClick={() => onHome && window.scrollTo({ top: 0, behavior: "smooth" })} className="flex items-center gap-2 sm:gap-2.5" aria-label="RC iPhones - início">
          <img src="/brand/mark-white.png" alt="RC" className="h-5 w-auto sm:h-6" />
          <span className="text-[14px] tracking-wide text-white/90 font-light max-[374px]:hidden sm:text-[15px]">iPhones</span>
        </Link>
        <nav className="hidden lg:flex items-center lg:gap-3.5 xl:gap-6">
          {LINKS.map((l) => (
            <button key={l.key} data-testid={`nav-link-${l.key}`} onClick={() => go(l)} className={linkCls}>{l.label}</button>
          ))}
        </nav>
        <div className="flex items-center gap-1.5 sm:gap-2">
        <SearchButton go={go} />
        <CartButton />
        <ThemeToggle />
        <a
          data-testid="header-whatsapp-button"
          href={waLink(GENERAL_MESSAGE)}
          target="_blank"
          rel="noopener noreferrer"
          className="group flex items-center gap-2 rounded-full bg-white text-black px-3 py-2 text-sm font-semibold transition-transform duration-300 hover:scale-[1.03] active:scale-95 sm:px-4"
        >
          <WhatsAppIcon className="w-4 h-4 transition-transform duration-500 group-hover:rotate-[20deg]" />
          <span className="hidden xl:inline">Chamar no WhatsApp</span>
          <span className="xl:hidden">WhatsApp</span>
        </a>
        </div>
      </div>
      <MobileTabs go={go} />
    </div>
  </motion.header>
  );
};
